import React from 'react'
import WorkshopIntro from './WorkshopIntro'
import WorkshopIntroStream from './WorkshopIntroStream'
import PurchaseTickets from './PurchaseTickets'
import Heading from 'shared/ui/Heading'
import WorkshopMaterial from './WorkshopMaterial'
import FreeWorkshopRegistration from './FreeWorkshopRegistration'

/**
 * Want to make a workshop with custom material?
 * 1. In `data/workshops.js`, set the `contentTemplate` flag.
 * 2. Add to the switch statement.
 * 3. Do whatever you want.
 */

export default function WorkshopBody({ ws }) {
  switch (ws.contentTemplate) {
    case 'free-reducers':
      return <FreeReducers ws={ws} />
    case 'react-advanced':
      return <ReactAdvanced ws={ws} />
    case 'node-fundamentals':
      return <NodeFundamentals ws={ws} />
    case 'typescript-first-day':
      return <TypeScriptFirstDay ws={ws} />
    case 'typescript-second-day':
      return <TypeScriptSecondDay ws={ws} />

    // The core workshop has three modes, it can be
    // the traditional two-day workshop, or we are also
    // selling day one and day two as separate events:
    case 'react-core-first-day':
      return <ReactCoreFirstDay ws={ws} />
    case 'react-core-second-day':
      return <ReactCoreSecondDay ws={ws} />
    case 'react-core':
      return <ReactCoreTwoDay ws={ws} />
    default:
      throw Error(`ws.contentTemplate is unrecognized for ${ws.id}`)
  }
}

/****************************************
  Workshop Body Templates:
*****************************************/

/**
 * Core Workshop Variations:
 */

function ReactCoreTwoDay({ ws }) {
  return (
    <div className="spacing-large">
      <WorkshopIntro />
      <PurchaseTickets ws={ws} />
      <hr />

      <section className="spacing">
        <Heading as="h2" size={2}>
          Who is this workshop for?
        </Heading>
        <p>
          <strong>Beginners?</strong> Absolutely. If all you've done is the official React tutorial
          then you are ready for this workshop. Even if you haven't done any React at all, this
          workshop is for you.
        </p>
        <p>
          <strong>Intermediate?</strong> Yes. This workshop along with {ws.companion} were designed
          for those with less than 3 years of typical experience.
        </p>
        <p>
          <strong>Advanced?</strong> Yes. While some of this workshop will be review, we regularly
          hear from our advanced attendees that they picked up some valuable nuggets of information
          that they didn't know. You might know most of the topics, but we'll leave you with the
          vocabulary and examples the help you level-up your team.
        </p>
      </section>

      <section className="spacing">
        <Heading as="h2" size={2}>
          Pre-Requisites
        </Heading>
        <WorkshopMaterial iconColor="blue" items={['JS Basics', 'DOM Basics']} />
      </section>

      <section className="spacing">
        <Heading as="h2" size={2}>
          Topics Covered
        </Heading>
        <WorkshopMaterial
          iconColor="green"
          items={[
            'All Hooks Based',
            'Custom Hooks',
            'Declarative Mental Model',
            'Local and Application State',
            'React Context',
            'Context Abstractions',
            'Side-Effects (Network API Calls and more)',
            'Data Flow',
            'React Router (Single Page Applications)',
            'Authentication',
            'Component Abstractions',
            'Accessibility',
            'Controlled and Uncontrolled Form Fields',
            'Controlled and Uncontrolled Components',
            'Performance Optimizations',
          ]}
        />
      </section>
    </div>
  )
}

function ReactCoreFirstDay({ ws }) {
  return (
    <div className="spacing-large">
      <WorkshopIntro />
      <PurchaseTickets ws={ws} />
      <hr />

      <section className="spacing">
        <Heading as="h2" size={2}>
          Who is this workshop for?
        </Heading>
        <p>
          <strong>Beginners?</strong> Absolutely. If all you've done is the official React tutorial
          then you are ready for this workshop. Even if you haven't done any React at all, this
          workshop is for you.
        </p>
        <p>
          <strong>Intermediate?</strong> Yes. This workshop along with {ws.companion} were designed
          for those with less than 3 years of typical experience.
        </p>
        <p>
          <strong>Advanced?</strong> Yes. While some of this workshop will be review, we regularly
          hear from our advanced attendees that they picked up some valuable nuggets of information
          that they didn't know. You might know most of the topics, but we'll leave you with the
          vocabulary and examples the help you level-up your team.
        </p>
      </section>

      <section className="spacing">
        <Heading as="h2" size={2}>
          Pre-Requisites
        </Heading>
        <WorkshopMaterial iconColor="blue" items={['JS Basics', 'DOM Basics']} />
      </section>

      <section className="spacing">
        <Heading as="h2" size={2}>
          Topics Covered
        </Heading>
        <WorkshopMaterial
          iconColor="green"
          items={[
            'All Hooks Based',
            'Custom Hooks',
            'Declarative Mental Model',
            'Local and Application State',
            'Side-Effects with useEffect',
            'State Lifting (State Sharing)',
            'Imperative React with Refs',
            'React Router (Single Page Applications)',
            'Component Abstractions',
            'Controlled vs Uncontrolled Fields',
          ]}
        />
      </section>
    </div>
  )
}

function ReactCoreSecondDay({ ws }) {
  return (
    <div className="spacing-large">
      <WorkshopIntro />
      <PurchaseTickets ws={ws} />
      <hr />

      <section className="spacing">
        <Heading as="h2" size={2}>
          Who is this workshop for?
        </Heading>
        <p>
          <strong>Beginners?</strong> Absolutely. If all you've done is the official React tutorial
          then you are ready for this workshop. Even if you haven't done any React at all, this
          workshop is for you.
        </p>
        <p>
          <strong>Intermediate?</strong> Yes. This workshop along with {ws.companion} were designed
          for those with less than 3 years of typical experience.
        </p>
        <p>
          <strong>Advanced?</strong> Yes. While some of this workshop will be review, we regularly
          hear from our advanced attendees that they picked up some valuable nuggets of information
          that they didn't know. You might know most of the topics, but we'll leave you with the
          vocabulary and examples the help you level-up your team.
        </p>
      </section>

      <section className="spacing">
        <Heading as="h2" size={2}>
          Pre-Requisites
        </Heading>
        <p>
          Before attending "{ws.title}", attendees should understand these topics which we teach in
          our "{ws.companion}". If you wont be attending "{ws.companion}" and you're not sure if
          you'll be able to follow along with {ws.title},{' '}
          <a href="https://app.questionable.io/start/vSVmL6pe">
            we created a quiz that will test your skills
          </a>{' '}
          with the material from "{ws.companion}".
        </p>
        <WorkshopMaterial
          iconColor="blue"
          items={[
            'JSX up to intermediate level',
            'Declarative Mental Model',
            'Functional Components',
            'Hooks up to intermediate level',
            'Local state with useState',
            'State Lifting (State Sharing) Basics',
            'useEffect up to intermediate level',
            'Controlled vs Uncontrolled Fields',
            'Basic knowledge of refs',
          ]}
        />
      </section>

      <section className="spacing">
        <Heading as="h2" size={2}>
          Topics Covered
        </Heading>
        <WorkshopMaterial
          iconColor="green"
          items={[
            'All Hooks Based',
            'Custom Hooks',
            'React Context',
            'Context Abstractions',
            'State Lifting and Prop Drilling',
            'Authentication',
            'Data Loading Strategies',
            'React Architecture',
            'Controlled and Uncontrolled Form Fields',
            'Performance Optimizations',
          ]}
        />
      </section>
    </div>
  )
}

/**
 * Advanced
 */

function ReactAdvanced({ ws }) {
  return (
    <div className="spacing-large">
      <WorkshopIntro />
      <PurchaseTickets ws={ws} />
      <hr />

      <section className="spacing">
        <Heading as="h2" size={2}>
          Who is this workshop for?
        </Heading>
        <p>
          <strong>Beginners?</strong> Maybe. If you're brand new to React you'll probably be lost in
          most of this material, but you will pick up some main ideas that will help you grow your
          React knowledge
        </p>
        <p>
          <strong>Intermediate?</strong> Probably. This workshop is one of the more advanced topics
          that we cover so we will probably challenge you a bit. It assumes you know the
          pre-requisites listed below fairly well, so you'll have to be the judge if you're ready.
          But even if some of the pre-requisites are new to you, you can probably brush up on them
          and be ready for this workshop.
        </p>
        <p>
          <strong>Advanced?</strong> Yes. This workshop is designed for those who have seen a bunch
          of other advanced material online and they're still wanting more. If you area already
          advanced in React, this is content is probably just what you need.
        </p>
      </section>

      <section className="spacing">
        <Heading as="h2" size={2}>
          Pre-Requisites
        </Heading>
        <p>
          Before attending {ws.title}, attendees should understand these topics.{' '}
          <a href="https://app.questionable.io/start/QWe4Sf8S">
            We suggest taking this quiz that assesses your knowledge
          </a>{' '}
          of that core material.
        </p>
        <WorkshopMaterial
          // To Stay consistent, we're using green for the main topics
          // and blue for any pre-requisites
          iconColor="blue"
          items={[
            'Function Components',
            'Hooks API',
            'Custom Hooks',
            'Declarative Mental Model',
            'Local State',
            'Side Effects',
            'Data Flow and Context',
            'Controlled vs Uncontrolled Form Fields',
          ]}
        />
      </section>

      <section className="spacing">
        <Heading as="h2" size={2}>
          Topics Covered
        </Heading>
        <WorkshopMaterial
          // To Stay consistent, we're using green for the main topics
          // and blue for any pre-requisites
          iconColor="green"
          items={[
            'All Hooks Based',
            'Advanced Abstraction Patterns',
            'Compound Components',
            'Mutable Ref Patterns',
            'Imperative React',
            'Controlled/Uncontrolled Components',
            'React and Accessibility',
          ]}
        />
      </section>
    </div>
  )
}

/**
 * Node Fundamentals
 */

function NodeFundamentals({ ws }) {
  return (
    <div className="spacing-large">
      <WorkshopIntro />
      <PurchaseTickets ws={ws} />
      <hr />

      <section className="spacing">
        <Heading as="h2" size={2}>
          Who is this workshop for?
        </Heading>
        <p>
          <strong>Beginners?</strong> Absolutely. Whether you have a little bit of JS/Node
          experience or none at all, this workshop is for you. Just keep in mind that this isn't a
          beginner "programming" workshop. We expect that you know programming in at least on other
          language such as PHP, Ruby, Python, Java, C#, etc.
        </p>
        <p>
          <strong>Intermediate?</strong> Yes. There's a huge spectrum for JS and Node knowledge and
          we're certain you'll pick up on things that you weren't aware of, even if some of the
          material is review for you.
        </p>
        <p>
          <strong>Advanced?</strong> Maybe. Even if the material is review, we expect that you might
          pick up a few tricks here and there. At the very least, you'll pick up on some new ways to
          teach this type of material to others.
        </p>
      </section>

      <section className="spacing">
        <Heading as="h2" size={2}>
          Pre-Requisites
        </Heading>
        <p>Before attending {ws.title}, attendees should understand these topics:</p>
        <WorkshopMaterial
          // To Stay consistent, we're using green for the main topics
          // and blue for any pre-requisites
          iconColor="blue"
          items={[
            'Already know programming (in any language)',
            'Basic knowledge of web servers',
            'Basic knowledge of NPM will help',
          ]}
        />
      </section>

      <section className="spacing">
        <Heading as="h2" size={2}>
          Topics Covered
        </Heading>
        <WorkshopMaterial
          // To Stay consistent, we're using green for the main topics
          // and blue for any pre-requisites
          iconColor="green"
          items={[
            'Various ways to make functions',
            'Lexical Scope',
            'The unique parts of Arrow Functions',
            'Asynchronous Concepts',
            'Prevent IO-Blocking Code',
            'Promises',
            'Async/Await',
            'Chaining Expressions',
            'NPM',
            'Node Filesystem',
            'Basic Node Servers',
            'Express Servers',
            'Express Middleware',
            'Error Handling',
            'Input Validation',
            'CORS (Imagine finally understanding CORS!)',
            'Authentication with JWT',
          ]}
        />
      </section>
      <hr />
      <p className="text-small">
        Some material will be in TypeScript. Don't worry if you don't know TypeScript at all though.
        We use it very lightly and we'll explain things as we go. If you love TypeScript or if
        you're hesitantly curious about it, this will be great. If you already know you don't like
        TypeScript, we promise there's not too much and it won't detract from the JS/Node parts.
      </p>
    </div>
  )
}

/**
 * TypeScript
 */

function TypeScriptFirstDay({ ws }) {
  return (
    <div className="spacing-large">
      <WorkshopIntro />
      <PurchaseTickets ws={ws} />
      <hr />

      <section className="spacing">
        <Heading as="h2" size={2}>
          Who is this workshop for?
        </Heading>
        <p>
          <strong>TS Beginners?</strong> Absolutely. Whether you have a little bit of TypeScript
          experience or none at all, this workshop is for you. Just keep in mind that this isn't a
          beginner "programming" workshop. We expect that you are at an intermediate level in
          JavaScript
        </p>
        <p>
          <strong>TS Intermediate?</strong> Yes. There's a huge spectrum if TypeScript knowledge and
          we're certain you'll pick up on things that you weren't aware of, even if some of the
          material is review for you.
        </p>
        <p>
          <strong>TS Advanced?</strong> Maybe. Even if the material is review, we expect that you
          might pick up a few tricks here and there. At the very least, you'll pick up on some new
          ways to teach this type of material to others.
        </p>
      </section>

      <section className="spacing">
        <Heading as="h2" size={2}>
          Material
        </Heading>
        <p>
          <b>{ws.title}</b> is one of our brand new two-day workshops. You have the option of
          attending one or both days of the workshop depending on where you are in your React
          journey. Tickets for each day are purchased separately. The first day (this event) will be
          on the basics of TypeScript.
        </p>
        <p>
          Before attending <b>day 1</b>, attendees should have at least an{' '}
          <b>intermediate level of JavaScript experience</b> and be comfortable working with its DOM
          APIs.
        </p>
        <Heading as="h2" size={2}>
          Topics Covered
        </Heading>
        <WorkshopMaterial
          // To Stay consistent, we're using green for the main topics
          // and blue for any pre-requisites
          iconColor="green"
          items={[
            'Static vs. dynamic languages',
            'TypeScript the language vs. TypeScript the compiler',
            'Configuration options',
            'Inferred types',
            'Defining types and interfaces',
            'Unions and intersections',
          ]}
        />
      </section>
    </div>
  )
}

function TypeScriptSecondDay({ ws }) {
  return (
    <div className="spacing-large">
      <WorkshopIntro />
      <PurchaseTickets ws={ws} />
      <hr />

      <section className="spacing">
        <Heading as="h2" size={2}>
          Who is this workshop for?
        </Heading>
        <p>
          <strong>TS Beginners?</strong> Maybe. This workshop does expect that you have a certain
          amount of TypeScript knowledge already. See the pre-requisites below. We do have a another
          workshop scheduled to teach these pre-requisites if in case you want to take that one as
          well.
        </p>
        <p>
          <strong>TS Intermediate?</strong> Yes, this workshop was designed for you. Just make sure
          you meed the pre-requisites listed below.
        </p>
        <p>
          <strong>TS Advanced?</strong> Maybe. Even if the material is review, we expect that you
          might pick up a few tricks here and there. At the very least, you'll pick up on some new
          ways to teach this type of material to others.
        </p>
      </section>

      <section className="spacing">
        <Heading as="h2" size={2}>
          Material
        </Heading>
        <p>
          <b>{ws.title}</b> is one of our brand new two-day workshops. You have the option of
          attending one or both days of the workshop depending on where you are in your React
          journey. Tickets for each day are purchased separately.
        </p>
      </section>

      <section className="spacing">
        <Heading as="h2" size={2}>
          Pre-Requisites
        </Heading>
        <p>
          Before attending <b>day 2</b> of {ws.title}, attendees should understand the topics
          covered during day 1 (listed below) and have a working understanding of React. Our Core
          React Workshop is also a great prerequisite to day 2 of {ws.title}.
        </p>
        <WorkshopMaterial
          // To Stay consistent, we're using green for the main topics
          // and blue for any pre-requisites
          iconColor="blue"
          items={[
            'JSX',
            'Class and function components',
            'React hooks API',
            'Custom hooks',
            'Side effects',
            'Data flow and context',
          ]}
        />
      </section>

      <section className="spacing">
        <Heading as="h2" size={2}>
          Topics Covered
        </Heading>
        <WorkshopMaterial
          // To Stay consistent, we're using green for the main topics
          // and blue for any pre-requisites
          iconColor="green"
          items={[
            'Generics and overloads',
            'Adding types to class components',
            'Adding types to function components',
            'Typing hooks and context',
            'Generic Components',
          ]}
        />
      </section>
    </div>
  )
}

/**
 * Free Streams (Mini Workshops)
 */

function FreeReducers({ ws }) {
  return (
    <div className="spacing-large">
      <WorkshopIntroStream />
      <FreeWorkshopRegistration ws={ws}>
        <p>
          Have you heard of "state reducers" but not quite sure what it means or how they work? In
          this session we'll demystify the reducer pattern and show you why useReducer can improve
          your code and squash nasty bugs!
        </p>
      </FreeWorkshopRegistration>
      <hr />

      <section className="spacing">
        <Heading as="h2" size={2}>
          Pre-Requisites
        </Heading>
        <WorkshopMaterial
          // To Stay consistent, we're using green for the main topics
          // and blue for any pre-requisites
          iconColor="blue"
          items={['Beginner knowledge of React & Hooks', 'Knowledge of useState is a big plus']}
        />
      </section>

      <section className="spacing">
        <Heading as="h2" size={2}>
          Topics Covered
        </Heading>
        <WorkshopMaterial
          // To Stay consistent, we're using green for the main topics
          // and blue for any pre-requisites
          iconColor="green"
          items={['Local State', 'The Reducer Pattern', 'All About useReducer']}
        />
      </section>
    </div>
  )
}
